// ---------------------------------------------------------------------------
// DSS-26: Persisted block style system for editor preview + published output
// ---------------------------------------------------------------------------

import type { DssAnimation } from "@ease/page-builder";
import {
  generateBackgroundCss,
  type BackgroundConfig,
} from "./dss/background-controls";
import {
  generateTypographyCss,
  type TypographyConfig,
} from "./dss/typography-controls";
import {
  generateBoxModelCss,
  type SpacingConfig,
} from "./dss/box-model-editor";
import {
  generateBorderCss,
  type BorderConfig,
} from "./dss/border-controls";
import {
  generateResponsiveCss,
  type ResponsiveConfig,
} from "./dss/responsive-overrides";
import type { FilterConfig } from "./dss/filter-controls";
import type { TransformConfig } from "./dss/transform-builder";
import type { PositionConfig } from "./dss/position-controls";
import type { DeviceVisibility } from "./dss/device-visibility";

export type StyleState = "normal" | "hover" | "focus" | "active";

export const STYLE_STATES: readonly StyleState[] = [
  "normal",
  "hover",
  "focus",
  "active",
] as const;

export interface BlockSpacingStyles {
  padding: SpacingConfig;
  margin: SpacingConfig;
}

export interface BlockTextStyles {
  color?: string;
  align?: "left" | "center" | "right" | "justify";
}

export interface CustomHtmlAttribute {
  name: string;
  value: string;
}

export interface BlockStyleData {
  background?: BackgroundConfig | null;
  typography?: TypographyConfig;
  text?: BlockTextStyles;
  spacing?: BlockSpacingStyles;
  border?: BorderConfig;
  shadow?: string;
  textShadow?: string;
  filter?: FilterConfig;
  transform?: TransformConfig;
  positionConfig?: PositionConfig;
  display?: string;
  blendMode?: string;
  visibility?: DeviceVisibility;
  animation?: DssAnimation;
  responsive?: ResponsiveConfig;
  elementId?: string;
  classNames?: readonly string[];
  customAttributes?: readonly CustomHtmlAttribute[];
}

export interface BlockStyleStates {
  normal?: BlockStyleData;
  hover?: BlockStyleData;
  focus?: BlockStyleData;
  active?: BlockStyleData;
}

export interface StyleStateOverrides {
  _hoverStyle?: Record<string, string>;
  _focusStyle?: Record<string, string>;
  _activeStyle?: Record<string, string>;
}

export interface TransitionConfig {
  property?: string;
  duration?: string;
  easing?: string;
}

const DEFAULT_TRANSITION: TransitionConfig = {
  property: "all",
  duration: "200ms",
  easing: "ease",
};

const BLOCK_STYLE_PROP = "_styles";
const VALID_CSS_PROPERTY = /^[a-zA-Z-]+$/;
const VALID_SCOPED_CLASS = /^[a-zA-Z][a-zA-Z0-9_-]*$/;
const SAFE_COMPONENT_ID = /^[a-zA-Z0-9_-]{1,64}$/;
const SAFE_ELEMENT_ID = /^[a-zA-Z][a-zA-Z0-9_-]{0,63}$/;
const SAFE_CLASS_NAME = /^[a-zA-Z_-][a-zA-Z0-9_-]{0,63}$/;
const SAFE_ATTRIBUTE_NAME = /^(data-[a-z0-9_-]{1,64}|aria-[a-z0-9_-]{1,64}|role)$/;
const SAFE_ROLE_VALUE = /^[a-z0-9_-]{1,64}$/;
const ALLOWED_STYLE_PROPERTIES = new Set([
  "background",
  "backgroundColor",
  "backgroundImage",
  "backgroundPosition",
  "backgroundSize",
  "backgroundRepeat",
  "backgroundAttachment",
  "opacity",
  "color",
  "textAlign",
  "fontFamily",
  "fontSize",
  "fontWeight",
  "fontStyle",
  "textTransform",
  "textDecoration",
  "lineHeight",
  "letterSpacing",
  "wordSpacing",
  "padding",
  "margin",
  "display",
  "width",
  "height",
  "gap",
  "flexDirection",
  "border",
  "borderStyle",
  "borderColor",
  "borderWidth",
  "borderRadius",
  "boxShadow",
  "outline",
  "transform",
]);

let counter = 0;

function camelToKebab(value: string): string {
  return value.replace(/([A-Z])/g, "-$1").toLowerCase();
}

function kebabToCamel(value: string): string {
  return value.replace(/-([a-z])/g, (_match, letter: string) => letter.toUpperCase());
}

function parseCssDeclarations(css: string): Record<string, string> {
  const result: Record<string, string> = {};

  for (const declaration of css.split(";")) {
    const trimmed = declaration.trim();
    if (!trimmed) continue;

    const colonIndex = trimmed.indexOf(":");
    if (colonIndex <= 0) continue;

    const property = trimmed.slice(0, colonIndex).trim();
    const value = trimmed.slice(colonIndex + 1).trim();
    const camelProperty = kebabToCamel(property);
    if (!ALLOWED_STYLE_PROPERTIES.has(camelProperty)) continue;
    if (!value) continue;
    result[camelProperty] = value;
  }

  return result;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

export function generateScopedId(): string {
  counter += 1;
  const random = Math.random().toString(36).slice(2, 8);
  return `puck-el-${random}${counter}`;
}

export function resetScopedCounter(): void {
  counter = 0;
}

export function sanitizeCssProperty(key: string): string {
  if (!VALID_CSS_PROPERTY.test(key)) return "";
  return key;
}

export function sanitizeScopedClass(className: string): string {
  if (!VALID_SCOPED_CLASS.test(className) || className.length > 96) return "";
  return className;
}

export function sanitizeCssValue(value: string): string {
  let safe = value;
  safe = safe.replace(/[{}]/g, "");
  safe = safe.replace(/\/\*/g, "");
  safe = safe.replace(/\*\//g, "");
  safe = safe.replace(/<\/style>/gi, "");
  safe = safe.replace(/@import/gi, "");
  safe = safe.replace(/@charset/gi, "");
  safe = safe.replace(/expression\s*\(/gi, "");
  safe = safe.replace(/url\s*\(\s*["']?\s*javascript\s*:/gi, "");
  safe = safe.replace(/url\s*\(\s*["']?\s*data:\s*text\/html/gi, "");
  safe = safe.replace(/\\/g, "");
  return safe.trim();
}

export function sanitizeComponentId(id: string): string {
  const trimmed = id.trim();
  if (SAFE_COMPONENT_ID.test(trimmed)) return trimmed;

  return trimmed
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);
}

export function sanitizeElementId(id: string): string {
  const trimmed = id.trim();
  if (!trimmed) return "";
  if (SAFE_ELEMENT_ID.test(trimmed)) return trimmed;

  const normalized = trimmed
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64);

  if (!normalized) return "";
  return /^[a-zA-Z]/.test(normalized) ? normalized : "";
}

export function sanitizeClassNames(classNames: readonly string[] | undefined): string[] {
  if (!classNames) return [];

  const seen = new Set<string>();
  const sanitized: string[] = [];

  for (const className of classNames) {
    const trimmed = className.trim();
    if (!trimmed || !SAFE_CLASS_NAME.test(trimmed) || seen.has(trimmed)) {
      continue;
    }
    seen.add(trimmed);
    sanitized.push(trimmed);
    if (sanitized.length >= 50) break;
  }

  return sanitized;
}

export function sanitizeCustomHtmlAttributes(
  attributes: readonly CustomHtmlAttribute[] | undefined,
): CustomHtmlAttribute[] {
  if (!attributes) return [];

  const sanitized: CustomHtmlAttribute[] = [];
  const seen = new Set<string>();

  for (const attribute of attributes) {
    const normalizedName = attribute.name.trim().toLowerCase();
    if (!SAFE_ATTRIBUTE_NAME.test(normalizedName) || seen.has(normalizedName)) {
      continue;
    }

    const normalizedValue = attribute.value
      .replace(/[\u0000-\u001F\u007F]/g, "")
      .trim()
      .replace(/\s+/g, " ")
      .slice(0, 256);

    if (!normalizedValue) continue;
    if (normalizedName === "role" && !SAFE_ROLE_VALUE.test(normalizedValue)) {
      continue;
    }

    seen.add(normalizedName);
    sanitized.push({ name: normalizedName, value: normalizedValue });
    if (sanitized.length >= 20) break;
  }

  return sanitized;
}

export function buildScopedClassName(componentId: string): string {
  const safeId = sanitizeComponentId(componentId);
  return safeId ? `puck-block-${safeId}` : generateScopedId();
}

export function sanitizeStyleMap(
  styles: Record<string, string> | undefined,
): Record<string, string> {
  if (!styles) return {};

  return Object.fromEntries(
    Object.entries(styles)
      .map(([key, value]) => [key.includes("-") ? kebabToCamel(key) : key, value] as const)
      .filter(([key]) => ALLOWED_STYLE_PROPERTIES.has(key))
      .map(([key, value]) => [key, sanitizeCssValue(value)] as const)
      .filter((entry) => typeof entry[1] === "string" && entry[1].length > 0),
  );
}

export function blockStyleToStyleMap(styleData: BlockStyleData | undefined): Record<string, string> {
  if (!styleData) return {};

  const declarations: Record<string, string> = {};

  if (styleData.background) {
    Object.assign(
      declarations,
      parseCssDeclarations(generateBackgroundCss(styleData.background)),
    );
  }

  if (styleData.typography) {
    Object.assign(
      declarations,
      parseCssDeclarations(generateTypographyCss(styleData.typography)),
    );
  }

  if (styleData.spacing) {
    Object.assign(
      declarations,
      parseCssDeclarations(
        generateBoxModelCss(styleData.spacing.padding, styleData.spacing.margin),
      ),
    );
  }

  if (styleData.border) {
    Object.assign(
      declarations,
      parseCssDeclarations(generateBorderCss(styleData.border)),
    );
  }

  if (styleData.shadow && styleData.shadow !== "none") {
    declarations.boxShadow = styleData.shadow;
  }

  if (styleData.text?.color) {
    declarations.color = styleData.text.color;
  }

  if (styleData.text?.align) {
    declarations.textAlign = styleData.text.align;
  }

  return sanitizeStyleMap(declarations);
}

function cssPropsToString(props: Record<string, string>): string {
  return Object.entries(sanitizeStyleMap(props))
    .filter(([key]) => sanitizeCssProperty(camelToKebab(key)) !== "")
    .map(([key, value]) => {
      const kebab = sanitizeCssProperty(camelToKebab(key));
      if (!kebab) return "";
      return `${kebab}: ${sanitizeCssValue(value)}`;
    })
    .filter(Boolean)
    .join("; ");
}

function deriveHoverAnimationStyle(animation: DssAnimation | undefined): Record<string, string> {
  if (!animation) return {};

  const transforms: string[] = [];
  if (typeof animation.hoverScale === "number" && animation.hoverScale !== 1) {
    transforms.push(`scale(${animation.hoverScale})`);
  }
  if (typeof animation.hoverTranslateY === "number" && animation.hoverTranslateY !== 0) {
    transforms.push(`translateY(${animation.hoverTranslateY}px)`);
  }

  const style: Record<string, string> = {};
  if (transforms.length > 0) {
    style.transform = transforms.join(" ");
  }
  if (animation.hoverShadow && animation.hoverShadow !== "none") {
    style.boxShadow = animation.hoverShadow;
  }

  return style;
}

function deriveTransitionConfig(animation: DssAnimation | undefined): TransitionConfig | undefined {
  if (!animation) return undefined;

  const duration = typeof animation.durationMs === "number"
    ? `${animation.durationMs}ms`
    : animation.duration;
  const easing = animation.easing;
  const property = animation.transition;

  if (!duration && !easing && !property) {
    return undefined;
  }

  return {
    property: property ?? "all",
    duration: duration ?? DEFAULT_TRANSITION.duration,
    easing: easing ?? DEFAULT_TRANSITION.easing,
  };
}

export function extractBlockStyleStates(
  props: Record<string, unknown>,
): BlockStyleStates {
  if (isRecord(props._dssStyles)) {
    const dssStyles = props._dssStyles as Record<string, unknown>;
    const { states, ...base } = dssStyles;
    const result: BlockStyleStates = {};

    if (Object.keys(base).length > 0) {
      result.normal = base as BlockStyleData;
    }

    if (isRecord(states)) {
      for (const state of STYLE_STATES) {
        if (state === "normal") continue;
        const stateValue = states[state];
        if (isRecord(stateValue)) {
          result[state] = stateValue as BlockStyleData;
        }
      }
    }

    return result;
  }

  const value = props[BLOCK_STYLE_PROP];
  if (!isRecord(value)) {
    return {};
  }

  const result: BlockStyleStates = {};
  for (const state of STYLE_STATES) {
    const stateValue = value[state];
    if (isRecord(stateValue)) {
      result[state] = stateValue as BlockStyleData;
    }
  }

  return result;
}

export function extractBaseStyle(props: Record<string, unknown>): Record<string, string> {
  const states = extractBlockStyleStates(props);
  return blockStyleToStyleMap(states.normal);
}

export function extractStateOverrides(
  props: Record<string, unknown>,
): StyleStateOverrides {
  const states = extractBlockStyleStates(props);
  const overrides: StyleStateOverrides = {};

  if (states.hover) {
    overrides._hoverStyle = blockStyleToStyleMap(states.hover);
  }

  if (states.focus) {
    overrides._focusStyle = blockStyleToStyleMap(states.focus);
  }

  if (states.active) {
    overrides._activeStyle = blockStyleToStyleMap(states.active);
  }

  const animationHoverStyle = deriveHoverAnimationStyle(states.normal?.animation);
  if (Object.keys(animationHoverStyle).length > 0) {
    overrides._hoverStyle = {
      ...(overrides._hoverStyle ?? {}),
      ...animationHoverStyle,
    };
  }

  return overrides;
}

export function extractTransitionConfig(
  props: Record<string, unknown>,
): TransitionConfig | undefined {
  const states = extractBlockStyleStates(props);
  return deriveTransitionConfig(states.normal?.animation);
}

export function generateStateCSS(
  scopedClass: string,
  overrides: StyleStateOverrides,
  transitions?: TransitionConfig,
): string {
  const safeClass = sanitizeScopedClass(scopedClass);
  if (!safeClass) return "";

  const rules: string[] = [];
  const trans = transitions ?? DEFAULT_TRANSITION;
  const hoverStyle = sanitizeStyleMap(overrides._hoverStyle);
  const focusStyle = sanitizeStyleMap(overrides._focusStyle);
  const activeStyle = sanitizeStyleMap(overrides._activeStyle);

  const hasHover = Object.keys(hoverStyle).length > 0;
  const hasFocus = Object.keys(focusStyle).length > 0;
  const hasActive = Object.keys(activeStyle).length > 0;

  if (!hasHover && !hasFocus && !hasActive) {
    return "";
  }

  const transProp = sanitizeCssValue(trans.property ?? "all");
  const transDur = sanitizeCssValue(trans.duration ?? "200ms");
  const transEase = sanitizeCssValue(trans.easing ?? "ease");
  rules.push(`.${safeClass} { transition: ${transProp} ${transDur} ${transEase}; }`);

  if (hasHover) {
    rules.push(`.${safeClass}:hover { ${cssPropsToString(hoverStyle)}; }`);
  }

  if (hasFocus) {
    rules.push(`.${safeClass}:focus { ${cssPropsToString(focusStyle)}; }`);
  }

  if (hasActive) {
    rules.push(`.${safeClass}:active { ${cssPropsToString(activeStyle)}; }`);
  }

  return rules.join("\n");
}

export function generateResponsiveStateCSS(
  scopedClass: string,
  responsive: ResponsiveConfig | undefined,
): string {
  const safeClass = sanitizeScopedClass(scopedClass);
  if (!safeClass || !responsive) return "";

  const config: ResponsiveConfig = {
    desktop: responsive.desktop ?? {},
    tablet: responsive.tablet ?? {},
    mobile: responsive.mobile ?? {},
  };

  const tabletRules = Object.entries(config.tablet).filter(([, value]) => value !== undefined && value !== "");
  const mobileRules = Object.entries(config.mobile).filter(([, value]) => value !== undefined && value !== "");

  if (tabletRules.length === 0 && mobileRules.length === 0) {
    return "";
  }

  return generateResponsiveCss(config, `.${safeClass}`);
}

export function mergeStyleStates(
  baseStyle: Record<string, string>,
  overrides: StyleStateOverrides,
  transitions?: TransitionConfig,
): { style: Record<string, string>; cssRules: string; className: string } {
  const className = generateScopedId();
  const cssRules = generateStateCSS(className, overrides, transitions);

  return {
    style: sanitizeStyleMap(baseStyle),
    cssRules,
    className,
  };
}

export function hasStateOverride(
  overrides: StyleStateOverrides,
  state: StyleState,
): boolean {
  if (state === "normal") return false;

  const key = `_${state}Style` as keyof StyleStateOverrides;
  const value = overrides[key];
  return value !== undefined && Object.keys(value).length > 0;
}
